/**
 * @fileoverview Notifications popover + rail trigger.
 *
 * Foundry mapping: `shell-application__notifications-popover`. A click-popover
 * anchored to the left-rail "Notifications" item, opening to the right (Foundry
 * `bp6-popover-placement-right`). It lists `NotificationItem` rows in a
 * scrollable area (max-height 560px / width 480px, per the reference) with a
 * "View all notifications" footer that deep-links to the full `/notifications`
 * page.
 *
 * The rail dot ("unacknowledged" state) lights while there are unread items and
 * clears once the popover has been opened and closed — so the user actually
 * sees the unread tint before it's marked seen. State lives in
 * `useNotifications` (localStorage-backed).
 */

import { Icon, Popover, Tooltip } from "@blueprintjs/core";


import { NotificationItem } from "./notification-item";
import { useNotifications, type Notification } from "./notifications-data";

/** Presentational list — shared by the popover and the full-page route. */
export function NotificationsList({
  notifications,
  unreadIds,
}: {
  notifications: readonly Notification[];
  unreadIds: ReadonlySet<string>;
}) {
  if (notifications.length === 0) {
    return <div className="bp-notifications-empty">You&rsquo;re all caught up.</div>;
  }
  return (
    <div className="bp-notifications-list">
      {notifications.map((n) => (
        <NotificationItem key={n.id} notification={n} unread={unreadIds.has(n.id)} />
      ))}
    </div>
  );
}

/** Popover content: the list plus the "View all" footer. */
function NotificationsPanel({
  notifications,
  unreadIds,
}: {
  notifications: readonly Notification[];
  unreadIds: ReadonlySet<string>;
}) {
  return (
    <div role="dialog" aria-label="Notifications" className="bp-notifications-panel">
      <NotificationsList notifications={notifications} unreadIds={unreadIds} />
    </div>
  );
}

/**
 * The rail trigger. Mirrors `RailLink`'s markup (icon-wrap + label) so it sits
 * flush with the other rail items, with an orange dot in the icon-badge slot
 * when there are unread notifications. Acknowledges (clears the dot) on close.
 */
export function NotificationsRailButton({ collapsed = false }: { collapsed?: boolean }) {
  const { notifications, unreadCount, unreadIds, acknowledge } = useNotifications();

  const trigger = (
    <Tooltip
      content="Notifications"
      placement="right"
      // Only when collapsed (the label is visible when expanded). Follow the
      // page theme, and don't auto-open when focus returns after popover close.
      disabled={!collapsed}
      compact
      inheritDarkTheme={false}
      openOnTargetFocus={false}
    >
      <button
        type="button"
        data-testid="app-shell-notifications"
        className="bp-rail-item"
        aria-label="Notifications"
      >
        <span className="bp-rail-item-icon-wrap">
          <Icon icon="notifications" />
          {unreadCount > 0 ? (
            <span className="bp-rail-item-icon-badge">
              <span className="bp-icon-badge-dot" aria-hidden="true" />
            </span>
          ) : null}
        </span>
        <span className="bp-rail-item-label">Notifications</span>
      </button>
    </Tooltip>
  );

  return (
    <Popover
      interactionKind="click"
      placement="right-start"
      fill
      // Don't inherit the dark rail's theme — the list is a light-mode content
      // surface (white), so its rows render on white like Foundry's popover.
      inheritDarkTheme={false}
      popoverClassName="bp-notifications-popover"
      onClosed={acknowledge}
      content={<NotificationsPanel notifications={notifications} unreadIds={unreadIds} />}
    >
      {trigger}
    </Popover>
  );
}
